import React, { useState } from 'react'
import { useForm } from 'react-final-form'
import { SemanticWIDTHS } from 'semantic-ui-react'
import { toast } from 'react-toastify'

import { getCnpjData } from '../../../../../../../services/cnpj'
import { Masked } from '../../../../../../../components/Form'
import { masks } from '../../../../../../../utils/mask'
import { isCnpj } from '../../../../../../../utils/document'

import { sanitizeExternalPhone } from '../../../../../utils/formatPerson'

interface CnpjInputProps {
  name: string
  mask: string
  label: string
  width: SemanticWIDTHS
  onLoading?: (loading: boolean) => void
  onValueChange?: (values: any) => void
}

export const CnpjInput: React.FC<CnpjInputProps> = ({
  name,
  mask,
  label,
  width,
  onLoading
}) => {
  const form = useForm()
  const [currentValue, setCurrentValue] = useState<string>()

  const removeMask = (value: string): string => {
    return value.replace(/[^0-9]/g, '').trim()
  }

  const searchCnpjData = React.useCallback(
    (cnpj: string) => {
      Promise.resolve()
        .then(() => onLoading?.(true))
        .then(() => getCnpjData(cnpj))
        .then(data => {
          if (data) {
            console.log('Dados recebidos: ', data)
            setCurrentValue(cnpj) // Salva o CNPJ que foi buscado com sucesso

            const emailField = form.getFieldState('emailsFormatted')
            const phoneField = form.getFieldState('phoneFormatted')

            // Defensive coding with optional chaining and fallbacks
            const firstPhone = data.contato?.telefones?.[0] || ''
            const sanitizedPhone = firstPhone
              ? sanitizeExternalPhone(firstPhone)
              : ''
            const firstEmail = data.contato?.emails?.[0] || ''

            const email = emailField?.value || firstEmail
            const phone =
              phoneField?.value ||
              (sanitizedPhone ? masks.phone(`55${sanitizedPhone}`) : '')
            const partnerIdField = form.getFieldState('partnerId')

            const updates = {
              partnerId: partnerIdField?.value,
              name: data.razaoSocial || '',
              birthDateFormatted: data.dataAberturaFormatted || '',
              tradeName: data?.nomeFantasia || '',
              emailsFormatted: email,
              phoneFormatted: phone,
              postalCode: data.endereco?.cep || '',
              street: data.endereco?.logradouro || '',
              number: data.endereco?.numero || '',
              complement: data.endereco?.complemento || '',
              neighborhood: data.endereco?.bairro || '',
              city: data.endereco?.municipio || '',
              state: data.endereco?.uf || ''
            }
            form.mutators.setFieldValues(updates)
          }
        })
        .catch(err => {
          console.error('Error searching CNPJ:', err)
          toast.error('Erro ao buscar dados do CNPJ.')
        })
        .finally(() => onLoading?.(false))
    },
    [form, onLoading]
  )

  const handleValueChange = (values: any) => {
    form.change(name, values.value)
  }

  const handleBlur = React.useCallback(() => {
    const rawValue = form.getFieldState(name)?.value || ''
    const cnpj = removeMask(rawValue)

    if (cnpj && cnpj === currentValue) {
      return // Não busca se o valor for o mesmo da última busca
    }

    if (!cnpj || cnpj.length < 14) {
      return // Não faz nada se o campo estiver vazio ou incompleto
    }

    if (!isCnpj(cnpj)) {
      toast.error('O valor digitado não é um CNPJ válido')
      return
    }

    searchCnpjData(cnpj)
  }, [name, currentValue, form, searchCnpjData])

  return (
    <Masked
      name={name}
      mask={mask}
      label={label}
      width={width}
      onValueChange={handleValueChange}
      onBlur={handleBlur}
    />
  )
}
